import * as React from "react";
import { USAGE_MATCHSOURCE_MANUAL_MATCH, USAGE_MATCHSTATUS_DEFINITE_MATCH } from "../../../../Consts";
import { Delete16Regular } from "@fluentui/react-icons";
import { IBulkMatchTarget } from "../../../../types/usageTypes/IBulkMatchTarget";
import IconTextButton from "../../../iconTextButton/IconTextButton";
import SaveButtonDropdown from "../../../saveButtonDropdown/SaveButtonDropdown";

export interface IUsageBulkMatchPanelProps {
    ariaLabel: string;
    target: IBulkMatchTarget | null;
    submitting: boolean;
    onOpenTarget: (target: IBulkMatchTarget) => void;
    onRemoveTarget: () => void;
    onApply: (createMatchHistory: boolean) => void;
    onCancel: () => void;
    children?: React.ReactNode;
}

const displayValue = (value: string | undefined): string => {
    return value?.trim() || "—";
};

const UsageBulkMatchPanel: React.FC<IUsageBulkMatchPanelProps> = ({
    ariaLabel, target, submitting, onOpenTarget, onRemoveTarget, onApply, onCancel, children }) => {

        const isWorkTarget = target?.entityType === "Work";

        const renderTargetTitle = (selectedTarget: IBulkMatchTarget) => (
            <td>
                <a
                    href="#"
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();

                        onOpenTarget(selectedTarget);
                    }}
                >
                    {displayValue(selectedTarget.title)}
                </a>
            </td>
        );

        const renderMakeDefiniteAction = () => (
            <td className="bulkMatchActionCell">
                <SaveButtonDropdown
                    mainLabel="With History"
                    mainOnClick={() => onApply(true)}
                    options={[
                        {
                            label: "Without History",
                            onClick: () => onApply(false)
                        }
                    ]}
                    disabled={submitting}
                />
            </td>
        );

        const renderRemoveAction = () => (
            <td className="bulkMatchRemoveCell">
                <IconTextButton
                    text=""
                    icon={Delete16Regular}
                    tooltip="Remove selected target"
                    id="remove-bulk-match-target"
                    disabled={submitting}
                    onClick={onRemoveTarget}
                />
            </td>
        );

        return (
            <section className="bulkMatchPanel" aria-label={ariaLabel} data-testid="bulk-match-panel">
                <div className="bulkMatchPanelHeader">
                    <h3>{ariaLabel}</h3>

                    <button
                        type="button"
                        className={"button btn-defaultSecondary " + "noMargin bulkMatchCancelButton"}
                        onClick={onCancel}
                        disabled={submitting}
                        data-testid="cancel-bulk-match"
                    >
                        Cancel Bulk Match
                    </button>
                </div>
                {target === null
                ? children
                : (
                    <div className="bulkMatchTargetReview" data-testid="bulk-match-target-review">
                        <div className="bulkMatchTargetTableWrapper">
                            <table className="bulkMatchTargetTable">
                                {isWorkTarget
                                    ? (
                                        <>
                                            <thead>
                                                <tr>
                                                    <th>Status</th>
                                                    <th>Title</th>
                                                    <th>Writers</th>
                                                    <th>Publishers</th>
                                                    <th>Performers</th>
                                                    <th>Number</th>
                                                    <th>Source</th>
                                                    <th>Make Definite</th>
                                                    <th>Work Status</th>
                                                    <th>Remove</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                <tr>
                                                    <td>{USAGE_MATCHSTATUS_DEFINITE_MATCH}</td>
                                                    {renderTargetTitle(target)}
                                                    <td>{displayValue(target.writers)}</td>
                                                    <td>{displayValue(target.publishers)}</td>
                                                    <td>{displayValue(target.performers)}</td>
                                                    <td>{displayValue(target.number)}</td>
                                                    <td>{USAGE_MATCHSOURCE_MANUAL_MATCH}</td>
                                                    {renderMakeDefiniteAction()}
                                                    <td>{displayValue(target.targetStatus)}</td>
                                                    {renderRemoveAction()}
                                                </tr>
                                            </tbody>
                                        </>
                                    )
                                    : (
                                        <>
                                            <thead>
                                                <tr>
                                                    <th>Status</th>
                                                    <th>Type</th>
                                                    <th>ID 1</th>
                                                    <th>ID 2</th>
                                                    <th>Title</th>
                                                    <th>Episode</th>
                                                    <th>Source</th>
                                                    <th>Artist</th>
                                                    <th>Works Count</th>
                                                    <th>Contributor</th>
                                                    <th>Year of Production</th>
                                                    <th>Make Definite</th>
                                                    <th>Remove</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                <tr>
                                                    <td>{USAGE_MATCHSTATUS_DEFINITE_MATCH}</td>
                                                    <td>{displayValue(target.productType)}</td>
                                                    <td>{displayValue(target.id1)}</td>
                                                    <td>{displayValue(target.id2)}</td>
                                                    {renderTargetTitle(target)}
                                                    <td>{displayValue(target.episode)}</td>
                                                    <td>{USAGE_MATCHSOURCE_MANUAL_MATCH}</td>
                                                    <td>{displayValue(target.performers)}</td>
                                                    <td>{target.worksCount ?? "—"}</td>
                                                    <td>{displayValue(target.contributors)}</td>
                                                    <td>{target.yearOfProduction ?? "—"}</td>
                                                    {renderMakeDefiniteAction()}
                                                    {renderRemoveAction()}
                                                </tr>
                                            </tbody>
                                        </>
                                    )}
                            </table>
                        </div>
                    </div>
                )}
            </section>
        );
    };

export default UsageBulkMatchPanel;
